import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { useUpgradeModal } from '../../contexts/UpgradeModalContext';
import { useCatalog } from '../../contexts/CatalogContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { ApiDigitalAuthSubscription } from '../../services/auth/subscription.service';
import { ApiDigitalAuthCatalog } from '../../services/auth/catalog.service';
import { BillingDA } from '../../services/admin/billing.service';
import { authApiKey, authCompany } from '../../scripts/authentication.script';
import { dateFormat } from '../../scripts/date.time.format.script';
import { featureLabel } from '../../scripts/feature.labels';
import { getCyclePriceInfo } from '../../scripts/cycle.pricing';
import { payInvoice } from '../../scripts/billing.payment';
import { GlobalSwal } from '../../styles/swal.style';
import { CatalogPlan, CatalogVocabulary } from '../../models/catalog.plan.model';
import { CatalogAddOn } from '../../models/catalog.addon.model';
import { BillingInvoice } from '../../models/billing.model';
import { TERMS_URL } from '../../config/billing.config';

type WizardStep = 1 | 2 | 3;
type PostConfirmPhase = 'idle' | 'polling' | 'invoice_ready' | 'timeout';

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 30000;

const ModalUpgradePlan: React.FC = () => {
  const { lang } = StateLanguage();
  const navigate = useNavigate();
  const { show, highlightFeature, closeUpgradeModal } = useUpgradeModal();
  const { plans, addons, vocabulary, currentPlanId: catalogCurrentPlanId, currentBillingCycle: catalogCurrentCycle, cheapestPlanForFeature } = useCatalog();
  const { snapshot, refreshSubscription } = useSubscription();

  // Wizard state
  const [wizardStep, setWizardStep] = useState<WizardStep>(1);
  const [billingCycle, setBillingCycle] = useState<string>('monthly');
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [selectedAddonIds, setSelectedAddonIds] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Add-ons fetched for the selected plan+cycle
  const [planAddons, setPlanAddons] = useState<CatalogAddOn[]>([]);
  const [planAddonsLoading, setPlanAddonsLoading] = useState(false);
  const [planAddonsError, setPlanAddonsError] = useState(false);
  const lastFetchRef = useRef<string>('');

  // All invoices (for operator vs client detection) and payable subset (for pay button)
  const [allInvoices, setAllInvoices] = useState<BillingInvoice[]>([]);
  const [payableInvoices, setPayableInvoices] = useState<BillingInvoice[]>([]);
  const [payingCurrentPlan, setPayingCurrentPlan] = useState(false);

  // Post-confirmation invoice polling
  const [postConfirmPhase, setPostConfirmPhase] = useState<PostConfirmPhase>('idle');
  const [polledInvoice, setPolledInvoice] = useState<BillingInvoice | null>(null);
  const pollRef = useRef<{ interval: ReturnType<typeof setInterval> | null; timeout: ReturnType<typeof setTimeout> | null }>({ interval: null, timeout: null });

  // Source of truth: the subscription's plan, not the catalog's
  const currentPlanId = snapshot?.subscription?.plan?.id ?? catalogCurrentPlanId;
  const sortedPlans = [...plans].sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999));

  const isFirstChoice = snapshot?.subscription?.plan_chosen === false;
  const currentCycle = snapshot?.subscription?.plan?.current_billing_cycle ?? snapshot?.subscription?.current_billing_cycle ?? catalogCurrentCycle;

  // When modal opens, reset wizard and set initial state
  useEffect(() => {
    if (!show) return;

    setWizardStep(1);
    setSelectedAddonIds(new Set());
    setPlanAddons([]);
    setPlanAddonsLoading(false);
    setPlanAddonsError(false);
    lastFetchRef.current = '';
    setPostConfirmPhase('idle');
    setPolledInvoice(null);
    setTermsAccepted(false);

    // Set billing cycle from current
    const cycle = snapshot?.subscription?.plan?.current_billing_cycle ?? snapshot?.subscription?.current_billing_cycle ?? catalogCurrentCycle;
    setBillingCycle(cycle === 'yearly' ? 'yearly' : 'monthly');

    // Pre-select the recommended plan if opened from a feature denial
    if (highlightFeature) {
      const recommended = cheapestPlanForFeature(highlightFeature);
      if (recommended?.id) {
        setSelectedPlanId(recommended.id);
        return;
      }
    }

    // Pre-select the current plan
    setSelectedPlanId(currentPlanId ?? sortedPlans[0]?.id ?? null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // Load invoices when modal opens — full list for operator detection, payable subset for pay button
  useEffect(() => {
    if (!show) return;
    const company = authCompany();
    if (!company?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await BillingDA.getInvoices(company.id, 1, 100);
        if (cancelled) return;
        if (response.status === 200 && response.data) {
          const items: BillingInvoice[] = response.data.items ?? response.data.invoices ?? (Array.isArray(response.data) ? response.data : []);
          setAllInvoices(items);
          setPayableInvoices(items.filter(inv => inv.payable));
        }
      } catch {
        // Silently ignore
      }
    })();
    return () => { cancelled = true; };
  }, [show]);

  // Poll for new invoice after plan confirmation
  useEffect(() => {
    if (postConfirmPhase !== 'polling') return;
    const company = authCompany();
    if (!company?.id) { setPostConfirmPhase('timeout'); return; }
    const companyId = company.id;

    // Snapshot of known invoice IDs before confirmation
    const knownIds = new Set(payableInvoices.map(inv => inv.invoice_id));

    let cancelled = false;

    const poll = async () => {
      try {
        const response = await BillingDA.getInvoices(companyId, 1, 100);
        if (cancelled) return;
        if (response.status === 200 && response.data) {
          const items: BillingInvoice[] = response.data.items ?? response.data.invoices ?? (Array.isArray(response.data) ? response.data : []);
          // Find a new payable invoice that didn't exist before
          const newInvoice = items.find(inv => inv.payable && !knownIds.has(inv.invoice_id));
          if (newInvoice) {
            setPolledInvoice(newInvoice);
            setPostConfirmPhase('invoice_ready');
          }
        }
      } catch {
        // Keep polling — transient errors are expected
      }
    };

    // First poll immediately
    poll();

    pollRef.current.interval = setInterval(poll, POLL_INTERVAL_MS);
    pollRef.current.timeout = setTimeout(() => {
      if (!cancelled) setPostConfirmPhase('timeout');
    }, POLL_TIMEOUT_MS);

    return () => {
      cancelled = true;
      if (pollRef.current.interval) clearInterval(pollRef.current.interval);
      if (pollRef.current.timeout) clearTimeout(pollRef.current.timeout);
      pollRef.current = { interval: null, timeout: null };
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postConfirmPhase]);

  // Clean up polling when phase leaves 'polling'
  useEffect(() => {
    if (postConfirmPhase !== 'polling') {
      if (pollRef.current.interval) clearInterval(pollRef.current.interval);
      if (pollRef.current.timeout) clearTimeout(pollRef.current.timeout);
      pollRef.current = { interval: null, timeout: null };
    }
  }, [postConfirmPhase]);

  const interpolate = (template: string, vars: Record<string, string | number>): string => {
    let result = template;
    for (const [key, value] of Object.entries(vars)) {
      result = result.replace(`{${key}}`, String(value));
    }
    return result;
  };

  const fmtDate = useCallback((iso?: string): string => {
    if (!iso) return '';
    return dateFormat(lang.code, iso);
  }, [lang.code]);

  // --- Feature helpers ---

  const allFeatureKeys = (vocab: CatalogVocabulary | null): string[] => {
    const vocabKeys = vocab?.features?.map(f => f.key) ?? [];
    const planKeySet = new Set<string>();
    for (const p of plans) {
      if (p.features && typeof p.features === 'object') {
        for (const key of Object.keys(p.features)) planKeySet.add(key);
      }
    }
    for (const a of addons) {
      if (a.features && typeof a.features === 'object') {
        for (const key of Object.keys(a.features)) {
          if (a.features[key] === true) planKeySet.add(key);
        }
      }
    }
    const ordered: string[] = [];
    for (const key of vocabKeys) {
      if (planKeySet.has(key)) { ordered.push(key); planKeySet.delete(key); }
    }
    for (const key of planKeySet) ordered.push(key);
    return ordered;
  };

  const planHasFeature = (plan: CatalogPlan, featureKey: string): boolean => {
    if (!plan.features || typeof plan.features !== 'object') return false;
    return plan.features[featureKey] === true;
  };

  const findAddonForFeature = (plan: CatalogPlan, featureKey: string): CatalogAddOn | null => {
    const planId = plan.id;
    for (const addon of addons) {
      if (!addon.features || addon.features[featureKey] !== true) continue;
      const compat = addon.compatible_plans;
      if (!compat || compat.length === 0 || (planId && compat.includes(planId))) return addon;
    }
    return null;
  };

  const formatAddonPrice = (addon: CatalogAddOn): string => {
    const price = addon.price;
    const code = addon.currency?.code ?? 'CHF';
    if (price === undefined || price === null) return '';
    const cycle = addon.billing_cycle;
    const suffix = cycle === 'yearly' ? lang.labels.subscriptionPerYear
                 : cycle === 'monthly' ? lang.labels.subscriptionPerMonth
                 : '';
    return `${code} ${price.toFixed(2)}${suffix}`;
  };

  // --- Addon fetching ---

  const fetchAddonsForPlan = useCallback(async (planId: string, cycle: string) => {
    const fetchKey = `${planId}:${cycle}`;
    if (lastFetchRef.current === fetchKey) return;

    const apiKey = authApiKey();
    if (!apiKey) return;

    lastFetchRef.current = fetchKey;
    setPlanAddonsLoading(true);
    setPlanAddonsError(false);

    try {
      const response = await ApiDigitalAuthCatalog.getAddonsForPlan(apiKey, planId, cycle);
      if (response.status === 200 && response.data) {
        const data = response.data;
        const list: CatalogAddOn[] = Array.isArray(data) ? data : (data?.addons ?? []);
        for (const a of list) {
          if (!a.id && a._id) a.id = a._id;
        }
        setPlanAddons(list);

        // Remove incompatible add-ons from selection
        setSelectedAddonIds(prev => {
          const validIds = new Set(list.map(a => a.id ?? a._id ?? ''));
          const next = new Set<string>();
          let removed = false;
          for (const id of prev) {
            if (validIds.has(id)) { next.add(id); } else { removed = true; }
          }
          if (removed) {
            toast(lang.labels.subscriptionAddonsIncompatibleRemoved, { icon: '⚠️' });
          }
          return next;
        });
      } else {
        setPlanAddons([]);
        setPlanAddonsError(true);
      }
    } catch {
      setPlanAddons([]);
      setPlanAddonsError(true);
    } finally {
      setPlanAddonsLoading(false);
    }
  }, [lang.labels.subscriptionAddonsIncompatibleRemoved]);

  const handleSelectPlan = (planId: string) => {
    if (planId !== selectedPlanId) {
      lastFetchRef.current = '';
      setTermsAccepted(false);
    }
    setSelectedPlanId(planId);
  };

  const handleCycleChange = (newCycle: string) => {
    if (newCycle !== billingCycle) {
      lastFetchRef.current = '';
      setBillingCycle(newCycle);
      setTermsAccepted(false);
    }
  };

  const toggleAddon = (addonId: string) => {
    setSelectedAddonIds(prev => {
      const next = new Set(prev);
      if (next.has(addonId)) { next.delete(addonId); } else { next.add(addonId); }
      return next;
    });
    setTermsAccepted(false);
  };

  // --- Wizard navigation ---

  const goToStep = (step: WizardStep) => {
    if (step === 2 && selectedPlanId) {
      fetchAddonsForPlan(selectedPlanId, billingCycle);
    }
    if (step < 3) {
      setTermsAccepted(false);
    }
    setWizardStep(step);
  };

  // --- Price summary (HT only) ---

  const priceSummary = useMemo(() => {
    const selectedPlan = plans.find(p => p.id === selectedPlanId);
    if (!selectedPlan) return null;

    const info = getCyclePriceInfo(selectedPlan, billingCycle);
    const planPrice = info.periodPrice ?? 0;
    const currency = info.currencyCode;

    let addonsPrice = 0;
    for (const addonId of selectedAddonIds) {
      const addon = planAddons.find(a => (a.id ?? a._id) === addonId);
      if (addon?.price != null) addonsPrice += addon.price;
    }

    const totalHT = Math.round((planPrice + addonsPrice) * 100) / 100;
    return { planPrice, addonsPrice, totalHT, currency };
  }, [plans, planAddons, selectedPlanId, selectedAddonIds, billingCycle]);

  // --- Payable invoices ---

  const totalDue = payableInvoices.reduce((sum, inv) => sum + (inv.amount ?? 0), 0);
  const dueCurrency = payableInvoices[0]?.currency ?? 'CHF';

  const handlePayCurrentPlan = async () => {
    if (payableInvoices.length === 0) return;
    const company = authCompany();
    if (!company?.id) return;
    setPayingCurrentPlan(true);
    try {
      if (payableInvoices.length === 1) {
        const paid = await payInvoice(payableInvoices[0], company.id, navigate, {
          error: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          ok: lang.labels.okGotIt,
        });
        if (paid) closeUpgradeModal();
      } else {
        closeUpgradeModal();
        navigate('/main/admin/subscription/invoices');
      }
    } finally {
      setPayingCurrentPlan(false);
    }
  };

  // --- Post-confirm actions ---

  const handlePayPolledInvoice = async () => {
    if (!polledInvoice) return;
    const company = authCompany();
    if (!company?.id) return;
    const paid = await payInvoice(polledInvoice, company.id, navigate, {
      error: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
      ok: lang.labels.okGotIt,
    });
    if (paid) closeUpgradeModal();
  };

  // --- Submit handler (no SweetAlert confirmation) ---

  const handleConfirm = async () => {
    if (!selectedPlanId || submitting) return;

    const apiKey = authApiKey();
    if (!apiKey) return;
    const subId = snapshot?.subscription?.id;
    if (!subId) {
      GlobalSwal.fire({ text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain, icon: 'error' });
      return;
    }

    setSubmitting(true);
    try {
      let response;
      if (isFirstChoice) {
        const addonIdsArray = Array.from(selectedAddonIds);
        response = await ApiDigitalAuthSubscription.choosePlan(apiKey, subId, selectedPlanId, billingCycle, addonIdsArray);
      } else {
        response = await ApiDigitalAuthSubscription.changePlan(apiKey, subId, selectedPlanId, billingCycle);
      }

      if (response.status === 200 || response.status === 201) {
        try { sessionStorage.removeItem('da_subscription_snapshot'); } catch {}
        await refreshSubscription();

        // If add-ons were selected in change mode, activate them one by one
        if (!isFirstChoice && selectedAddonIds.size > 0) {
          for (const addonId of selectedAddonIds) {
            try {
              await ApiDigitalAuthSubscription.activateAddon(apiKey, subId, addonId);
            } catch {
              // Continue — partial activation is acceptable
            }
          }
          await refreshSubscription();
        }

        // Enter polling phase — wait for the invoice to be created asynchronously
        setPostConfirmPhase('polling');
      } else {
        const errorCode = response.data?.code;
        if (errorCode === 'PLAN_DENIED') {
          GlobalSwal.fire({ text: lang.labels.subscriptionPlanDenied, icon: 'error', confirmButtonText: lang.labels.okGotIt });
        } else if (response.status === 409 && errorCode === 'PLAN_CHANGE_BLOCKED_BY_ADDONS') {
          GlobalSwal.fire({ text: lang.labels.subscriptionAddonBlocksChange, icon: 'warning', confirmButtonText: lang.labels.okGotIt });
        } else if (response.status === 409 && errorCode === 'PLAN_CHOICE_REQUIRED') {
          GlobalSwal.fire({ text: lang.labels.subscriptionErrorPlanChoiceRequired, icon: 'warning', confirmButtonText: lang.labels.okGotIt });
        } else if (response.status === 409 && errorCode === 'NOTHING_TO_UNDO') {
          GlobalSwal.fire({ text: lang.labels.subscriptionErrorNothingToUndo, icon: 'info', confirmButtonText: lang.labels.okGotIt });
        } else {
          GlobalSwal.fire({ text: response.data?.message ?? response.message, icon: 'error', confirmButtonText: lang.labels.okGotIt });
        }
      }
    } catch {
      GlobalSwal.fire({ text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain, icon: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  // --- Derived state ---

  const selectedPlan = sortedPlans.find(p => p.id === selectedPlanId) ?? null;
  const isSamePlan = selectedPlan?.id === currentPlanId;
  const isSameCycle = billingCycle === currentCycle;
  const planChosen = snapshot?.subscription?.plan_chosen === true;
  const isActive = snapshot?.subscription?.status === 'active';
  const isCurrent = isSamePlan && isSameCycle && (planChosen || isActive);
  const isTrial = snapshot?.subscription?.status === 'trial';
  const trialEnd = snapshot?.subscription?.trial_end;

  const features = allFeatureKeys(vocabulary);

  const getDirectionForPlan = (plan: CatalogPlan): string | undefined => plan.direction?.[billingCycle];
  const getEffectiveDateForPlan = (plan: CatalogPlan): string | undefined => plan.effective_date?.[billingCycle];

  // Modal title
  const modalTitle = isFirstChoice
    ? lang.labels.subscriptionChooseMyOffer
    : lang.labels.subscriptionModalTitleChange;

  if (!show) return null;

  // --- Wizard stepper ---
  const renderWizardStepper = () => {
    const steps: { num: WizardStep; label: string }[] = [
      { num: 1, label: lang.labels.subscriptionWizardStepPlan },
      { num: 2, label: lang.labels.subscriptionWizardStepAddons },
      { num: 3, label: lang.labels.subscriptionWizardStepSummary },
    ];

    return (
      <div className="d-flex justify-content-center align-items-center gap-5 mb-8">
        {steps.map((s, idx) => (
          <React.Fragment key={s.num}>
            {idx > 0 && <div className="border-top flex-grow-0" style={{ width: '40px' }}></div>}
            <div className="d-flex align-items-center gap-2">
              <span
                className={`badge badge-circle fw-bold fs-7 ${
                  wizardStep === s.num ? 'badge-primary' :
                  wizardStep > s.num ? 'badge-light-primary' : 'badge-light'
                }`}
                style={{ width: '30px', height: '30px', lineHeight: '30px' }}
              >
                {s.num}
              </span>
              <span className={`fw-bold fs-6 ${wizardStep === s.num ? 'text-primary' : 'text-gray-500'}`}>
                {s.label}
              </span>
            </div>
          </React.Fragment>
        ))}
      </div>
    );
  };

  // --- Step 1: Plan selection (cards + feature comparison) ---
  const renderStep1 = () => {
    const handleCardKeyDown = (e: React.KeyboardEvent, planId: string | undefined) => {
      if (!planId) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleSelectPlan(planId);
      }
    };

    return (
      <>
        {/* Billing cycle toggle — explicit buttons (nav-group) */}
        <div className="d-flex justify-content-center mb-8">
          <div className="nav-group nav-group-outline mx-auto" data-kt-buttons="true">
            <button
              type="button"
              className={`btn btn-color-gray-400 btn-active btn-active-secondary px-6 py-3 me-2 ${billingCycle === 'monthly' ? 'active' : ''}`}
              onClick={() => handleCycleChange('monthly')}
            >
              {lang.labels.monthly}
            </button>
            <button
              type="button"
              className={`btn btn-color-gray-400 btn-active btn-active-secondary px-6 py-3 ${billingCycle === 'yearly' ? 'active' : ''}`}
              onClick={() => handleCycleChange('yearly')}
            >
              {lang.labels.annual}
            </button>
          </div>
        </div>

        {/* Plan cards — fully clickable, accessible radio group */}
        <div className="row g-5 mb-8" role="radiogroup" aria-label={lang.labels.subscriptionWizardStepPlan}>
          {sortedPlans.map(plan => {
            const isSelected = plan.id === selectedPlanId;
            const isPlanCurrent = plan.id === currentPlanId;
            const info = getCyclePriceInfo(plan, billingCycle);

            return (
              <div className="col-md-4" key={plan.id}>
                <div
                  className={`plan-card plan-card--grid btn btn-outline btn-color-dark d-flex text-start p-6 h-100 ${isSelected ? 'btn-active btn-active-primary active' : ''}`}
                  role="radio"
                  aria-checked={isSelected}
                  tabIndex={0}
                  onClick={() => plan.id && handleSelectPlan(plan.id)}
                  onKeyDown={(e) => handleCardKeyDown(e, plan.id)}
                >
                  {/* Radio dot + Name */}
                  <div className="d-flex align-items-center mb-3">
                    <div className="form-check form-check-custom form-check-success me-4">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="modal-plan-select"
                        checked={isSelected}
                        readOnly
                        tabIndex={-1}
                      />
                    </div>
                    <div>
                      <h3 className="fw-bolder fs-3 mb-0">{plan.name}</h3>
                      {isPlanCurrent && (
                        <span className="badge badge-light-primary fs-8 mt-1">{lang.labels.subscriptionYourCurrentPlan}</span>
                      )}
                    </div>
                  </div>

                  {/* Price */}
                  <div className="plan-card__price">
                    {info.periodPrice === undefined || info.periodPrice === null ? (
                      <span className="plan-card__secondary fs-7">{lang.labels.subscriptionNoPricingForCycle}</span>
                    ) : info.periodPrice === 0 ? (
                      <span className="fs-2 fw-bolder">{lang.labels.subscriptionFreePlan}</span>
                    ) : info.monthlyEquivalent !== undefined ? (
                      <>
                        <div>
                          <span className="fs-2 fw-bolder plan-card__amount">{info.currencyCode} {info.monthlyEquivalent.toFixed(2)}</span>
                          <span className="plan-card__secondary fs-7 ms-1">{lang.labels.subscriptionEquivPerMonth}</span>
                        </div>
                        <div className="plan-card__secondary plan-card__amount fs-8">
                          {lang.labels.subscriptionBilledPerYear.replace('{amount}', `${info.currencyCode} ${info.periodPrice.toFixed(2)}`)}
                        </div>
                        {info.savingsPercent !== undefined && info.savingsPercent > 0 && (
                          <span className="badge badge-light-success fs-8 mt-1">
                            {lang.labels.subscriptionSavingsPercent.replace('{percent}', String(info.savingsPercent))}
                          </span>
                        )}
                      </>
                    ) : (
                      <>
                        <span className="fs-2 fw-bolder plan-card__amount">{info.currencyCode} {info.periodPrice.toFixed(2)}</span>
                        <span className="plan-card__secondary fs-7 ms-1">{lang.labels.subscriptionPerMonth}</span>
                      </>
                    )}
                  </div>

                  {/* Description */}
                  {plan.description && (
                    <div className="plan-card__secondary fs-7 mt-2">{plan.description}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Feature comparison table (read-only — selection happens via cards above) */}
        <div className="table-responsive">
          <table className="table table-row-bordered table-row-gray-200 align-middle gs-5 gy-4 m-0">
            <thead>
              <tr className="fw-bolder text-gray-700 bg-light">
                <th className="min-w-200px">{lang.labels.features}</th>
                {sortedPlans.map(plan => {
                  const isSelected = plan.id === selectedPlanId;
                  return (
                    <th key={plan.id} className={`text-center min-w-120px${isSelected ? ' bg-light-primary' : ''}`}>
                      {plan.name}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {features.map(featureKey => (
                <tr key={featureKey}>
                  <td className="fw-bold text-gray-700">{featureLabel(lang, featureKey)}</td>
                  {sortedPlans.map(plan => {
                    const isSelected = plan.id === selectedPlanId;
                    const included = planHasFeature(plan, featureKey);
                    const addon = !included ? findAddonForFeature(plan, featureKey) : null;
                    return (
                      <td key={plan.id} className={`text-center${isSelected ? ' bg-light-primary' : ''}`}>
                        {included ? (
                          <span className="text-success fw-bold">{lang.labels.subscriptionIncluded}</span>
                        ) : addon ? (
                          <span className="text-warning fw-bold">{lang.labels.subscriptionOptionalAddon}</span>
                        ) : (
                          <span className="text-gray-400">&mdash;</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  };

  // --- Step 2: Add-ons ---
  const renderStep2 = () => (
    <>
      <h3 className="fw-bolder mb-5">{lang.labels.subscriptionSelectAddons}</h3>
      {planAddonsLoading ? (
        <div className="text-center py-10">
          <span className="spinner-border spinner-border-sm text-primary me-2"></span>
          <span className="text-gray-600">{lang.labels.subscriptionAddonsLoading}</span>
        </div>
      ) : planAddonsError ? (
        <div className="text-center py-10">
          <p className="text-gray-600 fs-6">{lang.labels.subscriptionAddonsLoadError}</p>
          <button
            className="btn btn-sm btn-light-primary mt-3"
            type="button"
            onClick={() => {
              lastFetchRef.current = '';
              if (selectedPlanId) fetchAddonsForPlan(selectedPlanId, billingCycle);
            }}
          >
            {lang.labels.retry}
          </button>
        </div>
      ) : planAddons.length === 0 ? (
        <div className="text-center py-10">
          <p className="text-gray-600 fs-6">{lang.labels.subscriptionNoAddonsSelected}</p>
        </div>
      ) : (
        <div className="row g-4">
          {planAddons.map(addon => {
            const addonId = addon.id ?? addon._id ?? '';
            const checked = selectedAddonIds.has(addonId);
            const addonFeatures = addon.features
              ? Object.entries(addon.features).filter(([, v]) => v === true).map(([k]) => featureLabel(lang, k)).join(', ')
              : '';
            return (
              <div className="col-md-6 col-lg-4" key={addonId}>
                <label
                  className={`border rounded p-5 h-100 d-flex flex-column cursor-pointer${checked ? ' border-primary bg-light-primary' : ''}`}
                >
                  <div className="d-flex align-items-start gap-3">
                    <input
                      type="checkbox"
                      className="form-check-input mt-1"
                      checked={checked}
                      onChange={() => toggleAddon(addonId)}
                    />
                    <div className="flex-grow-1">
                      <div className="fw-bolder fs-5 mb-1">{addon.name ?? addonId}</div>
                      {addon.description && (
                        <div className="text-gray-600 fs-7 mb-2">{addon.description}</div>
                      )}
                      {addonFeatures && (
                        <div className="text-gray-500 fs-8 mb-2">{addonFeatures}</div>
                      )}
                      <span className="fw-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {formatAddonPrice(addon)}
                      </span>
                    </div>
                  </div>
                </label>
              </div>
            );
          })}
        </div>
      )}

      {/* Running total */}
      {priceSummary && selectedAddonIds.size > 0 && (
        <div className="d-flex justify-content-between align-items-center border-top mt-5 pt-4">
          <span className="fw-bold text-gray-700">{lang.labels.subscriptionAddonsTotal}</span>
          <span className="fw-bolder fs-5" style={{ fontVariantNumeric: 'tabular-nums' }}>
            {priceSummary.currency} {priceSummary.addonsPrice.toFixed(2)}
          </span>
        </div>
      )}
    </>
  );

  // --- Step 3: Summary ---
  const renderStep3 = () => {
    const selectedAddons = planAddons.filter(a => selectedAddonIds.has(a.id ?? a._id ?? ''));
    const direction = selectedPlan ? getDirectionForPlan(selectedPlan) : undefined;
    const effectiveDate = selectedPlan ? getEffectiveDateForPlan(selectedPlan) : undefined;
    const isUpgrade = direction === 'upgrade';

    return (
      <>
        <h3 className="fw-bolder mb-5">{lang.labels.subscriptionPriceSummary}</h3>
        <table className="table table-borderless m-0">
          <tbody>
            {/* Plan line */}
            <tr>
              <td className="text-gray-700">
                {interpolate(lang.labels.subscriptionPlanPrice, { plan: selectedPlan?.name ?? '' })}
                <span className="text-gray-500 fs-8 ms-2">
                  ({billingCycle === 'yearly' ? lang.labels.annual : lang.labels.monthly})
                </span>
              </td>
              <td className="text-end fw-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {priceSummary?.currency} {priceSummary?.planPrice.toFixed(2)}
              </td>
            </tr>
            {/* Add-on lines */}
            {selectedAddons.map(addon => {
              const addonId = addon.id ?? addon._id ?? '';
              return (
                <tr key={addonId}>
                  <td className="text-gray-700 ps-5">{addon.name ?? addonId}</td>
                  <td className="text-end fw-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {addon.currency?.code ?? priceSummary?.currency ?? 'CHF'} {(addon.price ?? 0).toFixed(2)}
                  </td>
                </tr>
              );
            })}
            {/* Total HT */}
            <tr className="border-top">
              <td className="fw-bolder fs-5">{lang.labels.subscriptionTotalHT}</td>
              <td className="text-end fw-bolder fs-5" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {priceSummary?.currency} {priceSummary?.totalHT.toFixed(2)}
              </td>
            </tr>
          </tbody>
        </table>

        {/* VAT note */}
        <p className="text-muted fs-7 mt-4 mb-0">{lang.labels.subscriptionVatOnInvoiceNote}</p>

        {/* Disclosures */}
        <div className="mt-4">
          {isFirstChoice ? (
            <>
              <p className="text-muted fs-7 mb-1">
                {interpolate(lang.labels.subscriptionInvoiceIssuedNow, {
                  date: fmtDate((() => {
                    const base = trialEnd ? new Date(trialEnd) : new Date();
                    base.setDate(base.getDate() + 30);
                    return base.toISOString();
                  })()),
                })}
              </p>
              {trialEnd && (
                <p className="text-muted fs-7 mb-1">
                  {interpolate(lang.labels.subscriptionTrialUnchanged, {
                    date: fmtDate(trialEnd),
                  })}
                </p>
              )}
              <p className="text-muted fs-7 mb-1">{lang.labels.subscriptionPaymentFlexible}</p>
            </>
          ) : isTrial && trialEnd ? (
            /* Change during trial — upgrade or downgrade */
            isUpgrade ? (
              allInvoices.length > 0 ? (
                /* Client-chosen plan with first invoice (paid or not) → show difference */
                <>
                  <p className="text-muted fs-7 mb-1">
                    {interpolate(lang.labels.subscriptionTrialUpgradeNotice, {
                      amount: `${priceSummary?.currency ?? 'CHF'} ${(
                        (priceSummary?.totalHT ?? 0) - ((() => {
                          const currentPlan = plans.find(p => p.id === currentPlanId);
                          if (!currentPlan) return 0;
                          const info = getCyclePriceInfo(currentPlan, billingCycle);
                          return info.periodPrice ?? 0;
                        })())
                      ).toFixed(2)}`,
                      date: fmtDate((() => {
                        const d = new Date(trialEnd);
                        d.setDate(d.getDate() + 30);
                        return d.toISOString();
                      })()),
                    })}
                  </p>
                  <p className="text-muted fs-7 mb-1">
                    {interpolate(lang.labels.subscriptionTrialUnchanged, { date: fmtDate(trialEnd) })}
                  </p>
                </>
              ) : (
                /* Operator-assigned plan (no first invoice) */
                <p className="text-muted fs-7 mb-1">
                  {interpolate(lang.labels.subscriptionTrialUpgradeOperatorNotice, {
                    plan: selectedPlan?.name ?? '',
                  })}
                </p>
              )
            ) : (
              /* Downgrade during trial */
              <p className="text-muted fs-7 mb-1">
                {interpolate(lang.labels.subscriptionTrialDowngradeNotice, {
                  plan: selectedPlan?.name ?? '',
                  date: fmtDate((() => {
                    const d = new Date(trialEnd);
                    if (billingCycle === 'yearly') {
                      d.setFullYear(d.getFullYear() + 1);
                    } else {
                      d.setMonth(d.getMonth() + 1);
                    }
                    return d.toISOString();
                  })()),
                  currentPlan: plans.find(p => p.id === currentPlanId)?.name ?? '',
                })}
              </p>
            )
          ) : (
            /* Outside trial — existing texts */
            <>
              <p className="text-muted fs-7 mb-1">{lang.labels.subscriptionProrataNotice}</p>
              <p className="text-muted fs-7 mb-1">
                {isUpgrade
                  ? lang.labels.subscriptionEffectImmediate
                  : effectiveDate
                    ? interpolate(lang.labels.subscriptionEffectAtDate, { date: fmtDate(effectiveDate) })
                    : lang.labels.subscriptionEffectImmediate
                }
              </p>
            </>
          )}
          {/* Renewal notice — cycle-specific */}
          <p className="text-muted fs-7 mb-1">
            {billingCycle === 'yearly'
              ? interpolate(lang.labels.subscriptionRenewalNoticeYearly, {
                  amount: `${priceSummary?.currency ?? 'CHF'} ${priceSummary?.totalHT.toFixed(2) ?? '0.00'}`,
                })
              : interpolate(lang.labels.subscriptionRenewalNoticeMonthly, {
                  amount: `${priceSummary?.currency ?? 'CHF'} ${priceSummary?.totalHT.toFixed(2) ?? '0.00'}`,
                })
            }
          </p>
          {/* Cancellation notice */}
          <p className="text-muted fs-7 mb-1">
            {lang.labels.subscriptionCancellationNotice}
          </p>
        </div>

        {/* Terms acceptance checkbox — copied from Register.tsx pattern */}
        <div className="fv-row mt-5">
          <label className="form-check form-check-custom form-check-solid mb-2">
            <input
              className="form-check-input"
              type="checkbox"
              checked={termsAccepted}
              onChange={() => setTermsAccepted(!termsAccepted)}
            />
            <span className="form-check-label fw-bold text-secondary fs-6">
              {lang.labels.iAgree}
              <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className="link-info cursor-pointer ms-1">
                {lang.labels.subscriptionTermsLink}
              </a>
              .
            </span>
          </label>
          {!termsAccepted && (
            <div className="text-danger fs-7 mt-1">
              {lang.labels.youMustAcceptTheTermsAndConditions}
            </div>
          )}
        </div>
      </>
    );
  };

  // --- Post-confirmation view ---
  const renderPostConfirm = () => {
    if (postConfirmPhase === 'polling') {
      return (
        <div className="text-center py-15">
          <span className="spinner-border text-primary mb-5" style={{ width: '3rem', height: '3rem' }}></span>
          <p className="fw-bold fs-4 text-gray-700 mb-0">{lang.labels.subscriptionConfirmedWaiting}</p>
        </div>
      );
    }

    if (postConfirmPhase === 'invoice_ready' && polledInvoice) {
      const amount = polledInvoice.amount != null ? polledInvoice.amount.toFixed(2) : '';
      const currency = polledInvoice.currency ?? 'CHF';
      return (
        <div className="text-center py-10">
          <div className="mb-5">
            <span className="svg-icon svg-icon-3x svg-icon-success">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M9.89557 13.4982L7.79487 11.2651C7.26967 10.7068 6.38251 10.7068 5.85731 11.2651C5.37559 11.7772 5.37559 12.5757 5.85731 13.0878L9.02396 16.4147C9.54916 16.9767 10.5765 16.9767 11.1385 16.4147L18.1427 9.04434C18.6244 8.53226 18.6244 7.73383 18.1427 7.22174C17.6175 6.66349 16.7303 6.66349 16.2051 7.22174L9.89557 13.4982Z" fill="currentColor" />
              </svg>
            </span>
          </div>
          <p className="fw-bold fs-4 text-gray-700 mb-2">{lang.labels.subscriptionInvoiceReady}</p>
          <p className="text-gray-600 fs-6 mb-8">
            {polledInvoice.number ?? polledInvoice.invoice_id} — {currency} {amount}
          </p>
          <div className="d-flex justify-content-center gap-4">
            {polledInvoice.payable && (
              <button className="btn btn-primary" type="button" onClick={handlePayPolledInvoice}>
                {lang.labels.billingPay}
              </button>
            )}
          </div>
        </div>
      );
    }

    if (postConfirmPhase === 'timeout') {
      return (
        <div className="text-center py-15">
          <p className="fw-bold fs-4 text-gray-700 mb-8">{lang.labels.subscriptionTimeoutMessage}</p>
          <button className="btn btn-primary" type="button" onClick={closeUpgradeModal}>
            {lang.labels.okGotIt}
          </button>
        </div>
      );
    }

    return null;
  };

  // --- Footer buttons per step ---
  const renderFooter = () => {
    if (wizardStep === 1) {
      return (
        <div className="d-flex align-items-center justify-content-between w-100">
          <div>
            {/* Pay current plan if applicable */}
            {selectedPlan && isCurrent && payableInvoices.length > 0 && (
              <button
                type="button"
                className="btn btn-light-primary"
                disabled={payingCurrentPlan}
                onClick={handlePayCurrentPlan}
              >
                {payingCurrentPlan
                  ? <span className="spinner-border spinner-border-sm"></span>
                  : interpolate(lang.labels.billingPayAmount, { amount: `${dueCurrency} ${totalDue.toFixed(2)}` })
                }
              </button>
            )}
          </div>
          <button
            className="btn btn-primary"
            type="button"
            disabled={!selectedPlanId}
            onClick={() => goToStep(2)}
          >
            {lang.labels.subscriptionWizardNext}
          </button>
        </div>
      );
    }

    if (wizardStep === 2) {
      return (
        <div className="d-flex justify-content-between w-100">
          <button className="btn btn-light" type="button" onClick={() => goToStep(1)}>
            {lang.labels.subscriptionWizardBack}
          </button>
          <button className="btn btn-primary" type="button" onClick={() => goToStep(3)}>
            {lang.labels.subscriptionWizardNext}
          </button>
        </div>
      );
    }

    // Step 3
    return (
      <div className="d-flex justify-content-between w-100">
        <button className="btn btn-light" type="button" onClick={() => goToStep(2)}>
          {lang.labels.subscriptionWizardBack}
        </button>
        <button
          className="btn btn-primary"
          type="button"
          disabled={submitting || !termsAccepted}
          onClick={handleConfirm}
        >
          {submitting
            ? <span className="spinner-border spinner-border-sm"></span>
            : isFirstChoice
              ? lang.labels.subscriptionWizardConfirmAndPay
              : lang.labels.subscriptionWizardConfirmChange
          }
        </button>
      </div>
    );
  };

  return (
    <>
      {/* Backdrop */}
      <div className="modal-backdrop fade show" onClick={closeUpgradeModal} />
      {/* Modal */}
      <div className="modal fade show d-block" tabIndex={-1} aria-modal="true" role="dialog">
        <div className="modal-dialog modal-xl">
          <div className="modal-content rounded" style={{ display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}>
            {/* Header */}
            <div className="modal-header border-0 pb-0">
              <h1 className="mb-0">{modalTitle}</h1>
              <div className="btn btn-sm btn-icon btn-active-color-primary" onClick={closeUpgradeModal}>
                <span className="svg-icon svg-icon-1">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <rect opacity="0.5" x="6" y="17.3137" width="16" height="2" rx="1" transform="rotate(-45 6 17.3137)" fill="currentColor" />
                    <rect x="7.41422" y="6" width="16" height="2" rx="1" transform="rotate(45 7.41422 6)" fill="currentColor" />
                  </svg>
                </span>
              </div>
            </div>
            {/* Scrollable body */}
            <div className="modal-body pt-5 px-5 px-xl-20" style={{ overflowY: 'auto', flex: '1 1 auto' }}>
              {postConfirmPhase !== 'idle' ? (
                renderPostConfirm()
              ) : (
                <>
                  {/* Wizard stepper */}
                  {renderWizardStepper()}

                  {/* Highlighted feature message */}
                  {wizardStep === 1 && highlightFeature && (
                    <div className="text-center text-muted fw-bold fs-5 mb-5">
                      {interpolate(lang.labels.subscriptionFeatureRequiresUpgrade, {
                        feature: featureLabel(lang, highlightFeature),
                      })}
                    </div>
                  )}

                  {/* Wizard steps */}
                  {wizardStep === 1 && renderStep1()}
                  {wizardStep === 2 && renderStep2()}
                  {wizardStep === 3 && renderStep3()}
                </>
              )}
            </div>
            {/* Footer — hidden during post-confirmation */}
            {postConfirmPhase === 'idle' && (
              <div className="modal-footer border-top px-5 px-xl-20 py-5" style={{ flex: '0 0 auto' }}>
                {renderFooter()}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ModalUpgradePlan;
